'use no memo'

import { useState } from 'react'
import { AppWindow, ArrowDown, ArrowUp, Check, ChevronDown, PanelRight, SlidersHorizontal } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useRouterState } from '@tanstack/react-router'
import { VIEW_TYPE_OPTIONS } from '../constants/viewTypeOptions'
import { useCurrentViewType } from '../hooks/useCurrentViewType'
import { CalendarDisplaySection, useResetCalendarDisplay } from './calendar/CalendarDisplaySection'
import { TimelineDisplaySection } from './timeline/TimelineDisplaySection'
import type { ReactNode } from 'react'
import type {
  BoardGroupBy,
  BoardSwimlanes,
  ListGroupBy,
  SortBy,
  SortDirection,
  ViewType,
} from '@/store/ProjectViewPreferencesStore'
import type { InboxGroupBy, RowField } from '@/store/ViewModeStore'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Drawer, DrawerContent, DrawerDescription, DrawerTitle, OpenOnlyTrigger, useDrawerTapGuard } from '@/components/ui/drawer'
import { useIsMobile } from '@/hooks/useMobile'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import useProjectViewPreferencesStore, { defaultPreferences } from '@/store/ProjectViewPreferencesStore'
import useViewModeStore, { ROW_FIELDS } from '@/store/ViewModeStore'
import useHideCompletedStore from '@/store/HideCompletedStore'
import useTimelineDisplayStore from '@/store/TimelineDisplayStore'
import { useWorkspaceMode } from '@/hooks/useWorkspaceMode'

const groupByOptions: Array<{ value: BoardGroupBy; labelKey: string }> = [
  { value: 'status', labelKey: 'board.status' },
  { value: 'priority', labelKey: 'board.priority' },
  { value: 'assignee', labelKey: 'board.assignee' },
]

const listGroupByOptions: Array<{ value: ListGroupBy; labelKey: string }> = [
  { value: 'status', labelKey: 'board.status' },
  { value: 'none', labelKey: 'layout.groupNone' },
]

const inboxGroupByOptions: Array<{ value: InboxGroupBy; labelKey: string }> = [
  { value: 'dueDate', labelKey: 'layout.sortDueDate' },
  { value: 'none', labelKey: 'layout.groupNone' },
]

const swimlaneOptions: Array<{ value: BoardSwimlanes; labelKey: string }> = [
  { value: 'none', labelKey: 'layout.groupNone' },
  { value: 'project', labelKey: 'layout.swimlanesProject' },
]

const sortByOptions: Array<{ value: SortBy; labelKey: string }> = [
  { value: 'manual', labelKey: 'layout.sortManual' },
  { value: 'priority', labelKey: 'board.priority' },
  { value: 'dueDate', labelKey: 'layout.sortDueDate' },
  { value: 'createdDate', labelKey: 'layout.sortCreated' },
  { value: 'name', labelKey: 'layout.sortName' },
]

// --- Shell -------------------------------------------------------------------

/** The toolbar trigger: every button in the bar shares one 30px height (design 25b). */
export const toolbarButtonClass = 'h-[30px] gap-1.5 rounded-lg px-2.5 text-[13px] font-medium'

function DisplayShell({
  title,
  children,
  onReset,
}: {
  title: string
  children: ReactNode
  onReset: () => void
}) {
  const { t } = useTranslation('projects')
  const isPhone = useIsMobile()
  const [open, setOpenState] = useState(false)
  const guardedOpen = useDrawerTapGuard(setOpenState, isPhone ? open : undefined)
  const setOpen = isPhone ? guardedOpen : setOpenState
  const trigger = (
    <Button
      type="button"
      variant="outline"
      size="sm"
      aria-label={isPhone ? t('display.title') : undefined}
      className={cn(toolbarButtonClass, 'bg-card phone:size-10 phone:border-transparent phone:bg-transparent phone:px-0')}
    >
      <SlidersHorizontal className="size-3.5 phone:size-5" />
      <span className="phone:hidden">{t('display.title')}</span>
    </Button>
  )
  const footer = (
    <div className="flex items-center justify-between border-t bg-secondary/40 px-4 py-2 text-[12px] text-muted-foreground phone:min-h-12 phone:text-sm">
      <button type="button" onClick={onReset} className="hover:text-foreground">
        {t('display.reset')}
      </button>
    </div>
  )

  // Phones get the same sections as a bottom sheet (design 36c).
  if (isPhone) {
    return (
      <>
        <OpenOnlyTrigger onOpen={() => setOpen(true)}>{trigger}</OpenOnlyTrigger>
        <Drawer open={open} onOpenChange={setOpen} direction="bottom" repositionInputs={false}>
        <DrawerContent onOpenAutoFocus={(event) => event.preventDefault()}>
          <DrawerTitle className="px-4 pb-1 pt-3 text-[17px] font-semibold">{title}</DrawerTitle>
          <DrawerDescription className="sr-only">{title}</DrawerDescription>
          <div className="min-h-0 flex-1 divide-y overflow-y-auto">{children}</div>
          {footer}
        </DrawerContent>
      </Drawer>
      </>
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      {/* Hangs from its button with a gap, never glued to the edge (design 25b). */}
      <PopoverContent align="end" sideOffset={8} collisionPadding={12} className="w-[300px] overflow-hidden rounded-xl p-0">
        <p className="px-4 pb-1 pt-3 text-[13px] font-semibold">{title}</p>
        <div className="divide-y">{children}</div>
        {footer}
      </PopoverContent>
    </Popover>
  )
}

function Section({ label, children }: { label?: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5 px-4 py-3">
      {label && (
        <p className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">{label}</p>
      )}
      {children}
    </div>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-h-8 items-center justify-between gap-3">
      <span className="text-[13px] text-foreground/80">{label}</span>
      {children}
    </div>
  )
}

function Segmented<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T
  options: Array<{ value: T; label: string; icon?: typeof PanelRight }>
  onChange: (value: T) => void
}) {
  return (
    <div role="radiogroup" className="grid auto-cols-fr grid-flow-col gap-1">
      {options.map(({ value: option, label, icon: Icon }) => (
        <button
          key={option}
          type="button"
          role="radio"
          aria-checked={value === option}
          onClick={() => onChange(option)}
          className={cn(
            'flex h-8 items-center justify-center gap-1.5 rounded-lg border text-[12.5px] transition-colors',
            value === option
              ? 'border-foreground bg-card font-medium text-foreground'
              : 'border-border text-muted-foreground hover:text-foreground',
          )}
        >
          {Icon && <Icon className="size-3.5" />}
          {label}
        </button>
      ))}
    </div>
  )
}

function OptionSelect<T extends string>({
  options,
  value,
  onChange,
}: {
  options: Array<{ value: T; labelKey: string }>
  value: T
  onChange: (value: T) => void
}) {
  const { t } = useTranslation('projects')
  const [open, setOpen] = useState(false)
  const selected = options.find((o) => o.value === value)
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-1 rounded-md px-1.5 py-1 text-[13px] font-medium text-foreground hover:bg-secondary"
        >
          {selected ? t(selected.labelKey) : value}
          <ChevronDown className="size-3 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-44 p-1" align="end">
        {options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            className="flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
            onClick={() => {
              onChange(opt.value)
              setOpen(false)
            }}
          >
            {t(opt.labelKey)}
            {opt.value === value && <Check className="size-3.5" />}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  )
}

function DensitySwitch<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T
  options: Array<{ value: T; label: string }>
  onChange: (value: T) => void
}) {
  return (
    <div role="radiogroup" className="flex items-center gap-0.5 rounded-lg bg-secondary p-0.5">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={value === option.value}
          onClick={() => onChange(option.value)}
          className={cn(
            'rounded-md px-2 py-0.5 text-[12px] font-medium transition-colors',
            value === option.value ? 'bg-card text-foreground shadow-xs' : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}

function RowFieldChips() {
  const { t } = useTranslation('projects')
  const hidden = useViewModeStore((s) => s.hiddenRowFields)
  const toggle = useViewModeStore((s) => s.toggleRowField)
  return (
    <div className="flex flex-wrap gap-1.5">
      {ROW_FIELDS.map((field: RowField) => {
        const on = !hidden.includes(field)
        return (
          <button
            key={field}
            type="button"
            aria-pressed={on}
            onClick={() => toggle(field)}
            className={cn(
              'h-7 rounded-full border px-3 text-[12px] transition-colors',
              on ? 'border-foreground bg-foreground text-background' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {t(`display.fields.${field}`)}
          </button>
        )
      })}
    </div>
  )
}

function ShowCompletedRow() {
  const { t } = useTranslation('projects')
  const hideCompleted = useHideCompletedStore((s) => s.hideCompleted)
  const toggle = useHideCompletedStore((s) => s.toggleHideCompleted)
  return (
    <Row label={t('display.showCompleted')}>
      <Switch checked={!hideCompleted} onCheckedChange={toggle} />
    </Row>
  )
}

/** Flattens sub-tasks into the list as rows; off, parents expand inline (design 28b). */
function ShowSubTasksRow() {
  const { t } = useTranslation('projects')
  const showSubTasks = useViewModeStore((s) => s.showSubTasks)
  const setShowSubTasks = useViewModeStore((s) => s.setShowSubTasks)
  return (
    <Row label={t('display.showSubTasks')}>
      <Switch checked={showSubTasks} onCheckedChange={setShowSubTasks} />
    </Row>
  )
}

// --- Display (formerly "Layout") -----------------------------------------------

export type LayoutScope =
  | { kind: 'project'; entityId: string }
  | { kind: 'space'; entityId: string }
  | { kind: 'taskPages' }

interface LayoutButtonProps {
  scope: LayoutScope
  /** Options that belong to one layout only (calendar, timeline), owned by that view. */
  layoutSection?: Partial<Record<ViewType, ReactNode>>
}

/**
 * Display (design 25b / 26b). Only options for the current layout: the
 * layout itself is switched in the segmented control, never here.
 */
export function LayoutButton({ scope, layoutSection }: LayoutButtonProps) {
  if (scope.kind === 'taskPages') {
    return <TaskPagesDisplay />
  }
  return <ProjectDisplay entityId={scope.entityId} layoutSection={layoutSection} />
}

function ProjectDisplay({
  entityId,
  layoutSection,
}: {
  entityId: string
  layoutSection?: Partial<Record<ViewType, ReactNode>>
}) {
  const { t } = useTranslation('projects')
  const { isPersonal } = useWorkspaceMode()
  const prefs = useProjectViewPreferencesStore((s) => s.preferences[entityId])
  const store = useProjectViewPreferencesStore()
  const resetRowDisplay = useViewModeStore((s) => s.resetDisplay)
  const resetCalendar = useResetCalendarDisplay(entityId)
  const resetTimeline = useTimelineDisplayStore((s) => s.reset)
  const viewType = useCurrentViewType(entityId)

  const storedGroupBy = prefs?.boardGroupBy ?? 'status'
  // Assignee grouping is meaningless in a single-user (personal) workspace.
  const boardGroupBy: BoardGroupBy = isPersonal && storedGroupBy === 'assignee' ? 'status' : storedGroupBy
  const detailsPanel = prefs?.detailsPanel ?? 'on'
  const sortBy: SortBy = prefs?.sortBy ?? 'manual'
  const sortDirection: SortDirection = prefs?.sortDirection ?? 'asc'
  const listGroupBy: ListGroupBy = prefs?.listGroupBy ?? 'status'
  const boardSwimlanes: BoardSwimlanes = prefs?.boardSwimlanes ?? 'none'
  const cardDensity = prefs?.density ?? 'simple'
  const layoutLabel = t(VIEW_TYPE_OPTIONS.find((o) => o.value === viewType)?.labelKey ?? 'views.list')

  const reset = () => {
    store.applyPreferences(entityId, {
      boardGroupBy: defaultPreferences.boardGroupBy,
      listGroupBy: defaultPreferences.listGroupBy,
      boardSwimlanes: 'none',
      sortBy: defaultPreferences.sortBy,
      sortDirection: defaultPreferences.sortDirection,
      density: defaultPreferences.density,
    })
    resetRowDisplay()
    if (viewType === 'calendar') resetCalendar()
    if (viewType === 'gantt') resetTimeline()
  }

  const sortRow = (
    <Row label={t('layout.sortBy')}>
      <div className="flex items-center gap-0.5">
        <OptionSelect options={sortByOptions} value={sortBy} onChange={(v) => store.setSortBy(entityId, v)} />
        {sortBy !== 'manual' && (
          <button
            type="button"
            aria-label={sortDirection === 'asc' ? t('layout.sortAsc') : t('layout.sortDesc')}
            title={sortDirection === 'asc' ? t('layout.sortAsc') : t('layout.sortDesc')}
            onClick={() => store.toggleSortDirection(entityId)}
            className="flex size-6 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground"
          >
            {sortDirection === 'asc' ? <ArrowUp className="size-3.5" /> : <ArrowDown className="size-3.5" />}
          </button>
        )}
      </div>
    </Row>
  )

  return (
    <DisplayShell title={t('display.titleFor', { layout: layoutLabel })} onReset={reset}>
      {viewType === 'list' && (
        <Section label={t('display.openTasksIn')}>
          <Segmented
            value={detailsPanel}
            onChange={(v) => store.setDetailsPanel(entityId, v)}
            options={[
              { value: 'on', label: t('display.sidePanel'), icon: PanelRight },
              { value: 'off', label: t('display.modal'), icon: AppWindow },
            ]}
          />
        </Section>
      )}

      {(viewType === 'list' || viewType === 'board') && (
        <Section label={t('display.arrange')}>
          {viewType === 'list' ? (
            <Row label={t('layout.groupBy')}>
              <OptionSelect options={listGroupByOptions} value={listGroupBy} onChange={(v) => store.setListGroupBy(entityId, v)} />
            </Row>
          ) : (
            <>
              <Row label={t('layout.groupBy')}>
                <OptionSelect
                  options={isPersonal ? groupByOptions.filter((o) => o.value !== 'assignee') : groupByOptions}
                  value={boardGroupBy}
                  onChange={(v) => store.setBoardGroupBy(entityId, v)}
                />
              </Row>
              <Row label={t('layout.swimlanes')}>
                <OptionSelect options={swimlaneOptions} value={boardSwimlanes} onChange={(v) => store.setBoardSwimlanes(entityId, v)} />
              </Row>
            </>
          )}
          {sortRow}
          {viewType === 'board' && (
            <Row label={t('display.cards')}>
              <DensitySwitch
                value={cardDensity}
                onChange={(v) => store.setDensity(entityId, v)}
                options={[
                  { value: 'simple', label: t('display.cardsSimple') },
                  { value: 'detailed', label: t('display.cardsDetailed') },
                ]}
              />
            </Row>
          )}
        </Section>
      )}

      {viewType === 'list' && (
        <Section label={t('display.showOnRows')}>
          <RowFieldChips />
        </Section>
      )}

      {viewType === 'calendar' && <CalendarDisplaySection entityId={entityId} />}
      {viewType === 'gantt' && <TimelineDisplaySection />}
      {layoutSection?.[viewType]}

      {/* The board has a Done column, so hiding completed doesn't apply there. */}
      {viewType !== 'board' && (
        <Section>
          {viewType === 'list' && <ShowSubTasksRow />}
          <ShowCompletedRow />
        </Section>
      )}
    </DisplayShell>
  )
}

function TaskPagesDisplay() {
  const { t } = useTranslation('projects')
  const viewMode = useViewModeStore((s) => s.viewMode)
  const setViewMode = useViewModeStore((s) => s.setViewMode)
  const inboxGroupBy = useViewModeStore((s) => s.inboxGroupBy)
  const setInboxGroupBy = useViewModeStore((s) => s.setInboxGroupBy)
  const listDensity = useViewModeStore((s) => s.listDensity)
  const setListDensity = useViewModeStore((s) => s.setListDensity)
  const resetDisplay = useViewModeStore((s) => s.resetDisplay)
  // Grouping only applies to the Inbox; the other task pages have their own.
  const isInbox = /\/tasks\/?$/.test(useRouterState({ select: (s) => s.location.pathname }))

  return (
    <DisplayShell title={t('display.titleFor', { layout: t('views.list') })} onReset={resetDisplay}>
      <Section label={t('display.openTasksIn')}>
        <Segmented
          value={viewMode}
          onChange={setViewMode}
          options={[
            { value: 'split', label: t('display.sidePanel'), icon: PanelRight },
            { value: 'simple', label: t('display.modal'), icon: AppWindow },
          ]}
        />
      </Section>
      <Section label={t('display.arrange')}>
        {isInbox && (
          <Row label={t('layout.groupBy')}>
            <OptionSelect options={inboxGroupByOptions} value={inboxGroupBy} onChange={setInboxGroupBy} />
          </Row>
        )}
        <Row label={t('layout.density')}>
          <DensitySwitch
            value={listDensity}
            onChange={setListDensity}
            options={[
              { value: 'comfortable', label: t('layout.comfortable') },
              { value: 'compact', label: t('layout.compact') },
            ]}
          />
        </Row>
      </Section>
      <Section label={t('display.showOnRows')}>
        <RowFieldChips />
      </Section>
      <Section>
        <ShowSubTasksRow />
      </Section>
    </DisplayShell>
  )
}
